import { CardSurface, TooltipTrigger, TagColored, SparkIcon } from '@spark';
import React, { useState, useMemo } from 'react';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import { colors } from '@/shared/tokens/colors';
import { StatusType } from './EntityExtractionsTable';
import { getTextDirectionStyle as defaultTextDirectionStyle } from '@/shared/utils/textUtils';
import '@/features/evaluation/styles/EntityTag.css';


const IconType = {
    SUCCESS: "success",
    WARNING: "warning",
    ERROR: "error",
}

const TOOLTIP_ICON_BY_TYPE = {
    [IconType.SUCCESS]: { slug: 'circle-check', color: colors.green },
    [IconType.WARNING]: { slug: 'circle-alert', color: colors.orange },
    [IconType.ERROR]: { slug: 'circle-x', color: colors.red },
};

// Match base body text size so status icons sit on the same visual line as labels.
const ENTITY_STATUS_ICON_SIZE = 14;

const TaggedSample = ({
    text,
    entities,
    getTextDirectionStyle = defaultTextDirectionStyle,
    allLabels,
    isGroundTruth,
    modelName,
    entityStatuses,
    disableCard = false,
    suppressWrongLocationMessage = false,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);


    const textDirectionStyle = getTextDirectionStyle(text || '');

    // Outline Spark icons per backend `icon_type` (success/warning/error).
    // A hidden placeholder keeps icon-less lines (icon_type === null) aligned.
    const renderTooltipIcon = (iconType) => {
        if (iconType === IconType.SUCCESS && isGroundTruth) return null;
        if (iconType === null) {
            return (
                <span
                    style={{
                        width: ENTITY_STATUS_ICON_SIZE,
                        height: ENTITY_STATUS_ICON_SIZE,
                        flexShrink: 0,
                        visibility: 'hidden',
                    }}
                />
            );
        }
        const icon = TOOLTIP_ICON_BY_TYPE[iconType];
        if (!icon) return null;
        return (
            <span
                style={{
                    display: 'inline-flex',
                    flexShrink: 0,
                    color: icon.color,
                    lineHeight: 1,
                    // Sit on the first text line (not centered across wrapped lines)
                    marginTop: '0.15em',
                }}
            >
                <SparkIcon slug={icon.slug} size={ENTITY_STATUS_ICON_SIZE} />
            </span>
        );
    };

    const getTooltipContent = (message, status, predictedCategory) => {
        if (!message) return null;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px', padding: '5px' }}>
                {(status === StatusType.PARTIAL || status === StatusType.MISSED) && (
                    <span style={{ fontWeight: 'bold' }}>'{predictedCategory}'</span>
                )}
                {message?.map((msg, idx) => (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '5px' }}>
                        {renderTooltipIcon(msg.icon_type)}
                        {typeof msg.text === 'string' && /<[^>]+>/.test(msg.text) ? (
                            <span
                                style={{ fontWeight: msg.is_bold ? 'bold' : 'normal' }}
                                dangerouslySetInnerHTML={{ __html: msg.text }}
                            />
                        ) : (
                            <span style={{ fontWeight: msg.is_bold ? 'bold' : 'normal' }}>{msg.text}</span>
                        )}
                    </div>
                ))}
            </div>
        );
    }
    const wrongLocationEntities = [];
    let lastIdx = 0;
    const parts = [];

    const allEntityStatuses = useMemo(() => {
        if (isGroundTruth) {
            return [...entities].sort((a, b) => a.start_index - b.start_index).map((entity) => {
                return {
                    status: StatusType.SUCCESS,
                    start_index: entity.start_index,
                    end_index: entity.start_index + entity.content.length,
                    predicted_label: entity.label,
                    message: [{
                        text: `${entity.label}`,
                        icon_type: IconType.SUCCESS,
                    }]
                }
            });
        }
        return [...entityStatuses].sort((a, b) => a.start_index - b.start_index);
    }, [entityStatuses, entities, isGroundTruth]);
    allEntityStatuses.forEach((statusResult, idx) => {
        // Get status from the evaluated results
        const status = statusResult.status;

        if (status === StatusType.WRONG_LOCATION) {
            wrongLocationEntities.push(statusResult);
            return; // con
        }

        // Create message using the utility function
        const message = statusResult.message;

        if (statusResult.start_index > lastIdx) {
            const nonEntityText = text.slice(lastIdx, statusResult.start_index);
            parts.push(
                <span
                    key={`t-${idx}`}
                    data-index={lastIdx}
                    style={getTextDirectionStyle(nonEntityText)}
                >
                    {nonEntityText}
                </span>
            );
        }

        if (statusResult.start_index < lastIdx) {
            console.error('entity overlaps previous entity', statusResult);
            return;
        }
        const entityContent = text.slice(statusResult.start_index, statusResult.end_index);
        lastIdx = statusResult.end_index;

        if (status === StatusType.PARTIAL) {
            const tooltipContent = getTooltipContent(message, status, statusResult.predicted_label);
            parts.push(
                <TooltipTrigger key={`e-${idx}`} content={tooltipContent} variant="light" disabled={!tooltipContent}>
                    <TagColored
                        className="entity-tag-inline"
                        noPadding
                        variant={getSparkTagVariant(statusResult.predicted_label, allLabels || [])}
                        label={(
                            <span className="entity-status-inline">
                                <span className="entity-status-inline-icon" style={{ color: colors.orange }}>
                                    <SparkIcon slug="circle-alert" size={ENTITY_STATUS_ICON_SIZE} />
                                </span>
                                <span style={getTextDirectionStyle(entityContent)}>{entityContent}</span>
                            </span>
                        )}
                    />
                </TooltipTrigger>
            );
        }
        else if (status === StatusType.SUCCESS) {
            const tooltipContent = getTooltipContent(message, status, statusResult.predicted_label);
            parts.push(
                <TooltipTrigger key={`e-${idx}`} content={tooltipContent} variant="light" disabled={!tooltipContent}>
                    <TagColored
                        className="entity-tag-inline"
                        noPadding
                        variant={getSparkTagVariant(statusResult.predicted_label, allLabels || [])}
                        label={<span style={getTextDirectionStyle(entityContent)}>{entityContent}</span>}
                    />
                </TooltipTrigger>
            );
        }
        else if (status === StatusType.ERROR) {
            const tooltipContent = getTooltipContent(message, status, statusResult.predicted_label);
            parts.push(
                <TooltipTrigger key={`e-${idx}`} content={tooltipContent} variant="light" disabled={!tooltipContent}>
                    <span
                        style={{
                            ...getTextDirectionStyle(entityContent),
                            color: colors.red,
                        }}
                    >
                        {entityContent}
                    </span>
                </TooltipTrigger>
            );
        } else if (status === StatusType.MISSED) {
            const tooltipContent = getTooltipContent(message, status, statusResult.expected_label);
            parts.push(
                <TooltipTrigger key={`e-${idx}`} content={tooltipContent} variant="light" disabled={!tooltipContent}>
                    <span
                        style={{
                            ...getTextDirectionStyle(entityContent),
                            color: colors.gray,
                        }}
                    >
                        {entityContent}
                    </span>
                </TooltipTrigger>
            );
        }
    });

    if (lastIdx < text?.length) {
        const remainingText = text?.slice(lastIdx);
        parts.push(
            <span
                key="t-end"
                data-index={lastIdx}
                style={getTextDirectionStyle(remainingText)}
            >
                {remainingText}
            </span>
        );
    }

    const content = (
        <div style={textDirectionStyle}>
            {isLoading && (
                <div style={{ textAlign: 'center', padding: '10px', color: '#666' }}>
                    Evaluating entities...
                </div>
            )}
            {error && (
                <div style={{ textAlign: 'center', padding: '10px', color: '#ff4d4f' }}>
                    Error: {error}
                </div>
            )}
            {!isLoading && !error && parts}
        </div>
    );

    return (
        <div style={textDirectionStyle}>
            {disableCard ? (
                <div style={{ padding: 0 }}>{content}</div>
            ) : (
                <CardSurface style={{ marginTop: -17, padding: 16 }}>
                    {content}
                </CardSurface>
            )}
            {!suppressWrongLocationMessage && wrongLocationEntities.length > 0 && (
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '5px' }}>
                    <span>
                        {modelName} extracted the following entities in the wrong location:
                    </span>
                    <span style={{ color: "gray" }}>
                        {wrongLocationEntities.map(ent => (
                            <>
                                {ent.predicted_label}:{' '}
                                <span style={getTextDirectionStyle(ent.details.actual_content)}>
                                    {ent.details.actual_content}
                                </span>
                                ;{' '}
                            </>
                        ))}
                    </span>
                </div>
            )}
        </div>
    );
};

export default TaggedSample; 